import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import type { ActionId } from '@cratered/shared';
import { renderMoleCard, type MoleLook } from '../game/mole';
import { getImage } from '../game/ugcImages';
import { TEAM_COLORS, TEAM_GLYPHS } from '../game/render';
import { helpOpen } from '../store';

export function Avatar({ name, url, speaking, large }: { name: string; url?: string; speaking?: boolean; large?: boolean }) {
  const cls = `avatar${speaking ? ' speaking' : ''}${large ? ' lg' : ''}`;
  return (
    <span class={cls} title={name} aria-label={name}>
      {url ? <img src={url} alt="" loading="lazy" /> : name.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function TeamDot({ team, enemy }: { team: number; enemy?: boolean }) {
  const color = enemy ? '#c0392b' : TEAM_COLORS[team % TEAM_COLORS.length];
  return (
    <span class="team-dot" style={{ background: color }} aria-label={enemy ? 'Raiders' : `Team ${team + 1}`}>
      {enemy ? '✕' : TEAM_GLYPHS[team % TEAM_GLYPHS.length]}
    </span>
  );
}

export function MoleCanvas({ look, aim, big, class: cls }: { look: MoleLook; aim?: boolean; big?: boolean; class?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const draw = () => (big ? renderMoleCard(el, look, aim, 3, 0.76) : renderMoleCard(el, look, aim));
    // Custom art arrives asynchronously; redraw once it has loaded.
    for (const url of [look.customBody, look.customHead]) if (url) getImage(url, draw);
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
  }, [look.race, look.body, look.outfit, look.teamColor, look.furColor, look.customBody, look.customHead, aim, big]);
  return <canvas ref={ref} class={cls} aria-hidden="true" />;
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div class="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div class="panel-head" style={{ padding: 0 }}>
          <h2>{title}</h2>
          <button class="btn icon ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function HelpModal() {
  if (!helpOpen.value) return null;
  return (
    <Modal title="How to play" onClose={() => (helpOpen.value = false)}>
      <p class="muted">
        Turn-based. On your turn, move your mole, pick a weapon, aim, hold to charge, release to fire. Knock the other team into the water or down to zero HP.
      </p>
      <div class="help-grid">
        <div>
          <b>Your turn</b>
          <p class="muted">Click one of your moles, then pick from the menu: Move, Ball, Grenade, Gun, Boot or Skip. Moving spends stamina and does not end the turn; any attack does.</p>
        </div>
        <div>
          <b>Aiming</b>
          <p class="muted">Press and drag from your mole: direction sets the angle, distance sets the power. Release to throw. With the gun, point the crosshair and click.</p>
        </div>
        <div>
          <b>Keyboard</b>
          <p class="muted">
            <kbd>1</kbd>–<kbd>7</kbd> menu · <kbd>Tab</kbd> next mole · <kbd>←</kbd> <kbd>→</kbd> walk / power · <kbd>↑</kbd> <kbd>↓</kbd> jump / angle · <kbd>Space</kbd> throw · <kbd>Esc</kbd> back
          </p>
        </div>
        <div>
          <b>Water and pits</b>
          <p class="muted">Anyone knocked into the water or off the map is out, and the shove counts as your kill. Headshots, direct hits and long shots pay bonus XP.</p>
        </div>
        <div>
          <b>Fair play</b>
          <p class="muted">Versus is cosmetics only. Perks from the Locker apply in Co-op and Campaign.</p>
        </div>
      </div>
    </Modal>
  );
}

export function ActionIcon({ id }: { id: ActionId }) {
  switch (id) {
    case 'move':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4" />
        </svg>
      );
    case 'ball':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M5 7c4 2 5 8 2 12M19 7c-4 2-5 8-2 12" fill="none" stroke="#1a120d" stroke-width="1.6" />
        </svg>
      );
    case 'grenade':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="14" r="7" />
          <rect x="10" y="3" width="4" height="5" rx="1" />
          <rect x="13" y="2" width="5" height="2" rx="1" />
        </svg>
      );
    case 'gun':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="1" y="10" width="19" height="3.5" rx="1" />
          <path d="M5 13.5l-1.5 6h3l1.5-6z" />
          <rect x="18" y="8.5" width="5" height="3" rx="1" />
          <rect x="8" y="7" width="6" height="3" rx="1" />
        </svg>
      );
    case 'sniper':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="7" />
          <path d="M12 2v5M12 17v5M2 12h5M17 12h5" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'boot':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M6 3h6v9l8 4v4H4v-6l2-2z" />
          <path d="M4 20h16" stroke="currentColor" stroke-width="2" />
        </svg>
      );
    case 'mortar':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2c3 3 4 8 4 13H8c0-5 1-10 4-13z" />
          <path d="M8 15l-3 5h4zM16 15l3 5h-4zM10 15h4v5h-4z" />
        </svg>
      );
    case 'rpg':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="2" y="10" width="14" height="5" rx="2.5" />
          <path d="M16 9l5 3.5-5 3.5z" />
          <path d="M2 10l-1-3h4zM2 15l-1 3h4z" />
        </svg>
      );
    case 'pickaxe':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 20L15 9" />
          <path d="M8 4c4-1 9 1 12 6-3-2-7-3-10-1z" fill="currentColor" />
        </svg>
      );
    case 'bat':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 21l3-3 11-13a2.5 2.5 0 0 1 3.5 3.5L7 19l-2 2z" />
          <circle cx="4.5" cy="19.5" r="2" />
        </svg>
      );
    case 'mallet':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="3" y="3" width="12" height="8" rx="2" />
          <path d="M8 11h4v10a1.5 1.5 0 0 1-3 0z" transform="rotate(-20 10 16)" />
          <rect x="15" y="4.5" width="6" height="5" rx="1.5" opacity="0.75" />
        </svg>
      );
    case 'pike':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 21L17 7" />
          <path d="M15 3h6v6" fill="currentColor" />
          <path d="M16 8l5-5" />
        </svg>
      );
    case 'bignade':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="14" r="9" />
          <rect x="10" y="1" width="4" height="5" rx="1" />
          <rect x="13" y="1" width="6" height="2" rx="1" />
          <circle cx="12" cy="14" r="4" fill="none" stroke="#1a120d" stroke-width="1.6" />
        </svg>
      );
    case 'cluster':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="9" r="6" />
          <circle cx="5" cy="18" r="3" />
          <circle cx="12" cy="20" r="3" />
          <circle cx="19" cy="18" r="3" />
        </svg>
      );
    case 'mine':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 17a9 9 0 0 1 18 0z" />
          <rect x="2" y="17" width="20" height="3" rx="1" />
          <path d="M12 3v4M6 6l2 3M18 6l-2 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      );
    case 'shotgun':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="1" y="9" width="17" height="3" rx="1" />
          <rect x="1" y="12.5" width="13" height="2.5" rx="1" />
          <path d="M14 15l-3 6h4l2-6z" />
          <path d="M18 9h5v3h-5z" />
        </svg>
      );
    case 'harpoon':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 21c3-2 4-6 9-9" />
          <path d="M12 12l8-8" />
          <path d="M14 4h6v6" />
          <path d="M17 7l-3 3" />
        </svg>
      );
    case 'grapple':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
          <path d="M12 2v9" />
          <path d="M5 13a7 7 0 0 0 14 0" />
          <path d="M5 13l-2-2M19 13l2-2" />
          <circle cx="12" cy="11" r="2" fill="currentColor" />
        </svg>
      );
    case 'platform':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="1" y="9" width="22" height="6" rx="1.5" />
          <path d="M8 9v6M16 9v6" stroke="#1a120d" stroke-width="1.4" />
          <path d="M6 15l-2 5M18 15l2 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      );
    case 'sandbags':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="2" y="15" width="9" height="6" rx="3" />
          <rect x="13" y="15" width="9" height="6" rx="3" />
          <rect x="7" y="9" width="10" height="6" rx="3" />
          <rect x="2" y="9" width="4" height="6" rx="2" />
          <rect x="18" y="9" width="4" height="6" rx="2" />
          <rect x="7" y="3" width="10" height="6" rx="3" />
        </svg>
      );
    case 'medkit':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="2" y="6" width="20" height="15" rx="3" />
          <rect x="8" y="3" width="8" height="4" rx="1.5" />
          <path d="M12 10v7M8.5 13.5h7" stroke="#1a120d" stroke-width="2.4" stroke-linecap="round" />
        </svg>
      );
    case 'repair':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14 7a4 4 0 0 1 5-4l-3 3 2 2 3-3a4 4 0 0 1-5 5L7 19a2 2 0 0 1-3-3z" fill="currentColor" />
        </svg>
      );
    case 'smg':
      // A stubby body with a long magazine and a folded stock: the burst gun, not the pistol.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="4" y="8" width="14" height="5" rx="1.5" />
          <rect x="18" y="9.5" width="5" height="2" rx="1" />
          <rect x="9.5" y="13" width="3.5" height="8" rx="1" />
          <path d="M15 13l-1 5h3l1-5z" />
          <path d="M4 9H1v5h2v-3h1z" />
        </svg>
      );
    case 'sticky':
      // A bomb with burrs all round it: it clings to whatever it lands on.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="14" r="6.5" />
          <circle cx="5" cy="11" r="1.6" />
          <circle cx="19" cy="11" r="1.6" />
          <circle cx="6" cy="19" r="1.6" />
          <circle cx="18" cy="19" r="1.6" />
          <circle cx="12" cy="22" r="1.5" />
          <rect x="10.5" y="4" width="3" height="5" rx="1" />
          <path d="M12 4c0-2 2-2 2-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
        </svg>
      );
    case 'rubber':
      // A ball at the end of a zigzag: four seconds of ricochets.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M2 21l4-9 4 9 4-9 3 6" />
          <circle cx="20" cy="5" r="3.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'airstrike':
      // A jet over three falling bombs.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M2 7l12 .5 2-3.5h3l-1.5 3.5L22 8l-4.5.5 1.5 3.5h-3l-2-3.5L2 9z" />
          <path d="M6 13v6M4 17l2 2 2-2M12 13v8M10 19l2 2 2-2M18 13v6M16 17l2 2 2-2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      );
    case 'massive':
      // A bomb that fills the tile, fuse sparking: the 400 coin crater.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="14" r="9.5" />
          <path d="M12 4.5V1.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
          <path d="M15 2.5l2-1.5M15.5 5.5l2.5-.5M9 2.5L7 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          <path d="M6.5 13a5.5 5.5 0 0 1 3.5-4.5" fill="none" stroke="#1a120d" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      );
    case 'crossbow':
      // Seen from above: the bow, its string and a bolt down the stock.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 10c4-5 14-5 18 0" />
          <path d="M3 10h18" />
          <path d="M12 3v18" />
          <path d="M9 6l3-3 3 3" />
          <path d="M9 21h6" />
        </svg>
      );
    case 'railgun':
      // A long coiled tube with a bolt leaving the muzzle: it goes through everything.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="1" y="9.5" width="19" height="5" rx="2" />
          <path d="M6 8v8M10 8v8M14 8v8" stroke="#1a120d" stroke-width="1.6" stroke-linecap="round" />
          <path d="M5 14.5l-1 6h3l1-6z" />
          <path d="M21 12h2M20.5 8.5l2-2M20.5 15.5l2 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      );
    case 'tnt':
      // Three sticks in a band, fuse lit.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="3" y="8" width="5" height="13" rx="1.5" />
          <rect x="9.5" y="8" width="5" height="13" rx="1.5" />
          <rect x="16" y="8" width="5" height="13" rx="1.5" />
          <rect x="2" y="12.5" width="20" height="3" fill="#1a120d" opacity="0.6" />
          <path d="M12 8V4c0-1.5 1.5-1.5 2-3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      );
    case 'catapult':
      // A base on wheels, the arm wound back with a boulder in its cup.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="16" width="16" height="3" rx="1" fill="currentColor" stroke="none" />
          <path d="M5 16L17 6" />
          <path d="M12 16l-2-5" />
          <circle cx="18.5" cy="4.5" r="2.8" fill="currentColor" stroke="none" />
          <circle cx="6" cy="21" r="1.8" fill="currentColor" stroke="none" />
          <circle cx="16" cy="21" r="1.8" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'teleport':
      // Two portals and the hop between them.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <ellipse cx="5.5" cy="12" rx="3" ry="8" />
          <ellipse cx="18.5" cy="12" rx="3" ry="8" fill="currentColor" />
          <path d="M9 12h5M11.5 9.5l2.5 2.5-2.5 2.5" />
        </svg>
      );
    case 'bridge':
      // A long deck on two piers with an arch beneath: it spans the whole gap.
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="1" y="7" width="22" height="4" rx="1" />
          <path d="M6 7v4M12 7v4M18 7v4" stroke="#1a120d" stroke-width="1.2" />
          <path d="M3 11v9M21 11v9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
          <path d="M3 20c3-8 15-8 18 0" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
        </svg>
      );
    case 'tunneler':
      // A boring machine: a boxy body on tracks with the drill cone out front, grooves on the bit.
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="2" y="7" width="11" height="10" rx="2" fill="currentColor" stroke="none" />
          <path d="M13 7l9 5-9 5z" fill="currentColor" stroke="none" />
          <path d="M15 9.5l3 1.5M15 14.5l3-1.5" stroke="#1a120d" stroke-width="1.4" />
          <path d="M4 20h9" />
          <circle cx="4" cy="20" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="13" cy="20" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'skip':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M5 5l9 7-9 7z" />
          <rect x="16" y="5" width="3" height="14" rx="1" />
        </svg>
      );
  }
}

export function Stars({ n }: { n: number }) {
  return (
    <span class="stars" aria-label={`${n} of 3 stars`}>
      {[1, 2, 3].map((i) => (
        <span key={i} class={i <= n ? '' : 'off'}>
          ★
        </span>
      ))}
    </span>
  );
}
